/**
 * Missing Middle "Navigation Conditions" reframe of the LEI pillars.
 *
 * Constants (CONDITION_STAGES, PILLAR_STAGE, conditionBand, BAND_TONE,
 * STAGE_DIAGNOSTIC_TAB) come from lib/missingMiddle.js — the single
 * source of truth. This module holds the LEI-overview-specific reading
 * aids (buildConditions, strongest/weakest, conversion efficiency,
 * navigation constraints, fix targets). No math changes.
 */
import {
  CONDITION_STAGES, PILLAR_STAGE, conditionBand, BAND_TONE, STAGE_DIAGNOSTIC_TAB,
} from './missingMiddle';

export { CONDITION_STAGES, PILLAR_STAGE, conditionBand, BAND_TONE, STAGE_DIAGNOSTIC_TAB };

/** Map overview pillar_scores → ordered condition stages (journey order). */
export function buildConditions(overviewData) {
  const byId = {};
  (overviewData?.pillar_scores || []).forEach((p) => { byId[p.id] = p; });
  return CONDITION_STAGES.map((s) => {
    const p = byId[s.pillarId];
    const score = p?.score ?? null;
    return {
      ...s,
      pillarShortName: p?.short_name || s.label,
      pillarName: p?.name || null,
      pillarWeight: typeof p?.weight === 'number' ? p.weight : null,
      score,
      band: conditionBand(score),
      measures: p?.measures || [],
    };
  });
}

export function strongestWeakest(conditions) {
  const present = (conditions || []).filter((c) => c.score != null);
  if (!present.length) return { strongest: null, weakest: null };
  const strongest = present.reduce((a, b) => (b.score > a.score ? b : a));
  const weakest = present.reduce((a, b) => (b.score < a.score ? b : a));
  return { strongest, weakest };
}

/** Conversion efficiency = Value ÷ Capability (the Missing Middle in one number). */
export function conversionEfficiency(conditions) {
  const cap = conditions?.find((c) => c.key === 'capability')?.score;
  const val = conditions?.find((c) => c.key === 'value')?.score;
  if (!cap || !val) return null;
  return Math.round((val / cap) * 100);
}

// Journey-stage key → Navigation Readiness Diagnostic component tab (from missingMiddle).

/** Best "fix" destination for a weak condition stage. */
export function stageFixTarget(key, orgQuery = '') {
  const tab = STAGE_DIAGNOSTIC_TAB[key];
  if (tab) return `/people-engine/diagnostic?component=${tab}${orgQuery}`;
  // Value stage (Business Impact) → Value Conversion Analysis.
  return `/gap-analysis${orgQuery ? `?${orgQuery.replace(/^&/, '')}` : ''}`;
}

/** Bottom conditions ranked as navigation constraints (High/Med/Low impact). */
export function navigationConstraints(conditions, limit = 3) {
  const present = (conditions || []).filter((c) => c.score != null);
  const ranked = [...present].sort((a, b) => a.score - b.score).slice(0, limit);
  const impacts = ['High', 'Medium', 'Low'];
  return ranked.map((c, i) => ({ ...c, impact: impacts[i] || 'Low' }));
}
